import { AppModal } from '@components'
import { thunkActionLoading } from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createEvolveMemberUseAction,
  getEvolveMemberUseAction,
  updateEvolveMemberUseAction,
  useAppDispatch,
  useAppSelector,
} from '@redux'
import { message } from 'antd'
import { t } from 'i18next'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Input, TextArea } from 'src/common'
import { CustomDragger } from 'src/common/upload/CustomDragger'
import { XCloseIcon } from 'src/components/Icon'
import SelectMedia from 'src/components/gallery/SelectMedia'
import SelectMediaModal from 'src/components/gallery/SelectMediaModal'
import { IEvolveMemberUseItem } from 'src/interfaces/evolve-landing-management'
import { EMediaCategory, EMediaType, IMediaItem } from 'src/interfaces/gallery'
import styled from 'styled-components'
import { IMemberUse, memberUseSchema } from './memberUseSchema'

type ContentDetailModalProps = {
  open: boolean
  isLoading: boolean
  onClose: () => void
  title?: string
  content?: ReactNode
  item?: IEvolveMemberUseItem | null
  changeLoading?: (value: boolean) => void
  noDefault: number
}

export const ContentDetailModal = (props: ContentDetailModalProps) => {
  const { open, onClose, isLoading, item, changeLoading, noDefault } = props
  const dispatch = useAppDispatch()

  const [imageSrc, setImageSrc] = useState(item?.media?.url ?? '')
  const [imageMobileSrc, setImageMobileSrc] = useState(
    item?.mediaMobile?.url ?? ''
  )
  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)
  const isCreatingMemberUse = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[thunkActionLoading.CREATE_MEMBER_USE_LOADING]
  )
  const isUpdatingMemberUse = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[thunkActionLoading.UPDATE_MEMBER_USE_LOADING]
  )

  const defaultValue: IMemberUse = {
    content: '',
    contentInDutch: '',
    mediaId: undefined as any,
    mediaMobileId: undefined as any,
    title: '',
    titleInDutch: '',
    no: noDefault,
  }
  const { control, handleSubmit, setValue, reset } = useForm<IMemberUse>({
    defaultValues: defaultValue,
    resolver: zodResolver(memberUseSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onSave = async (data: IMemberUse) => {
    try {
      if (item?.id) {
        const res = await dispatch(
          updateEvolveMemberUseAction({ body: data, id: item.id.toString() })
        ).unwrap()
        if (res.success) {
          message.success({
            content: res?.message ?? t('common:save_success'),
          })
          dispatch(getEvolveMemberUseAction())
          onClose()
        }
      } else {
        const res = await dispatch(createEvolveMemberUseAction(data)).unwrap()
        if (res.success) {
          message.success({
            content: res?.message ?? t('common:save_success'),
          })
          dispatch(getEvolveMemberUseAction())
          onClose()
        }
      }
    } catch (error: any) {
      message.error({
        content:
          error.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
    }
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
  }
  useEffect(() => {
    if (item?.id) {
      setValue('content', item?.content ?? '')
      setValue('contentInDutch', item?.contentInDutch ?? '')
      setValue('mediaId', item?.mediaId ?? 0)
      setValue('mediaMobileId', item?.mediaMobileId ?? 0)
      setValue('title', item?.title ?? '')
      setValue('titleInDutch', item?.titleInDutch ?? '')
      setValue('no', item?.no ?? noDefault)
      setImageSrc(item.media?.url ?? '')
      setImageMobileSrc(item.mediaMobile?.url ?? '')
    } else {
      reset(defaultValue)
      setImageSrc('')
      setImageMobileSrc('')
    }
  }, [open])

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center justify-between ">
          <div>
            <h1 className="m-0 text-[20px]">
              {item ? 'Edit new member' : 'Add new member'}
            </h1>
          </div>
          <div className="hover:opacity-75 cursor-pointer">
            <XCloseIcon width={16} height={16} onClick={onClose} />
          </div>
        </div>
      }
      childrenClassName="!mt-1"
    >
      <div className="mt-6">
        <form>
          <InputWrapperStyled>
            <Controller
              name={'title'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    alignment="col"
                    label="Title"
                    required
                    name="Title"
                    placeholder={t('evolveLanding:enter_title')}
                    onChange={onChange}
                    value={value}
                    errors={error?.message}
                  />
                )
              }}
            />
            <Controller
              name={'titleInDutch'}
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    alignment="col"
                    label="Title in Netherlands"
                    name="titleInDutch"
                    required
                    placeholder={t('evolveLanding:enter_title_in_NL')}
                    onChange={onChange}
                    value={value}
                    errors={error?.message}
                  />
                )
              }}
            />

            <Controller
              name="content"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    alignment="col"
                    label="Description"
                    required
                    name="content"
                    placeholder={t('evolveLanding:enter_description')}
                    onChange={onChange}
                    value={value}
                    errors={error?.message}
                    containerClassName="mt-3"
                  />
                )
              }}
            />

            <Controller
              name="contentInDutch"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    alignment="col"
                    label="Description in Netherlands"
                    name="contentInDutch"
                    required
                    onChange={onChange}
                    placeholder={t('evolveLanding:enter_description_in_NL')}
                    value={value}
                    errors={error?.message}
                    containerClassName="mt-3"
                  />
                )
              }}
            />

            <div className="flex flex-col">
              <Controller
                name="mediaId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  onChangeDesktopRef.current = onChange
                  return (
                    <CustomDragger
                      label="Image"
                      name="mediaId"
                      id="mediaId"
                      containerClassName="mt-3"
                      required
                      alignment="col"
                      onLoadEnd={(data) => {
                        setImageSrc((prev) => data.original ?? prev)
                        setValue('mediaId', data?.id ?? 0)
                        onChange(data?.id ?? 0)
                      }}
                      note={'1920 x 1080 resolution for better experience'}
                      errors={error?.message}
                      initResource={imageSrc}
                      reset={open}
                      allowFileTypes={['image/png', 'image/jpeg', 'image/jpg']}
                      limitFileSize={5}
                      changeLoading={changeLoading}
                      uploadType={EMediaType.IMAGE as any}
                      uploadCategory={EMediaCategory.HOME_PAGE}
                    />
                  )
                }}
              />
              <div className="flex self-end">
                <span className="required text-danger text-center ">
                  {'(1920 x 1080 resolution for better experience)'}
                </span>
              </div>
              <div className="flex self-end">
                <SelectMedia
                  isNotGrid
                  onClickSelectMedia={() => {
                    setSelectedField((pre) => 'desktop')
                    setShowModalSelectMedia(true)
                  }}
                  isLoadingMedia={
                    isLoading || isCreatingMemberUse || isUpdatingMemberUse
                  }
                />
              </div>
            </div>

            <div className="flex flex-col">
              <Controller
                name="mediaMobileId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  onChangeMobileRef.current = onChange
                  return (
                    <CustomDragger
                      label="Image mobile"
                      name="mediaMobileId"
                      id="mediaMobileId"
                      alignment="col"
                      containerClassName="mt-3"
                      required
                      onLoadEnd={(data) => {
                        setImageMobileSrc((prev) => data.original ?? prev)
                        setValue('mediaMobileId', data?.id ?? 0)
                        onChange(data?.id ?? 0)
                      }}
                      note={'1920 x 1080 resolution for better experience'}
                      errors={error?.message}
                      initResource={imageMobileSrc}
                      reset={open}
                      allowFileTypes={['image/png', 'image/jpeg', 'image/jpg']}
                      limitFileSize={5}
                      changeLoading={changeLoading}
                      uploadType={EMediaType.IMAGE as any}
                      uploadCategory={EMediaCategory.HOME_PAGE}
                    />
                  )
                }}
              />
              <div className="flex self-end">
                <span className="required text-danger  text-center ">
                  {'(720 x 480 resolution for better experience)'}
                </span>
              </div>
              <div className="flex self-end">
                <SelectMedia
                  isNotGrid
                  onClickSelectMedia={() => {
                    setSelectedField((pre) => 'mobile')
                    setShowModalSelectMedia(true)
                  }}
                  isLoadingMedia={
                    isLoading || isCreatingMemberUse || isUpdatingMemberUse
                  }
                />
              </div>
            </div>
          </InputWrapperStyled>

          <div className="mt-6">
            <div className="flex items-center mt-5 justify-end">
              <div className="flex items-center mt-5 justify-end">
                <Button
                  type="ghost"
                  size="middle"
                  className="submit__btn login-btn mr-10"
                  onClick={onClose}
                  disabled={
                    isLoading || isCreatingMemberUse || isUpdatingMemberUse
                  }
                >
                  {'Cancel'}
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  size="middle"
                  className="submit__btn login-btn"
                  loading={
                    isLoading || isCreatingMemberUse || isUpdatingMemberUse
                  }
                  disabled={
                    isLoading || isCreatingMemberUse || isUpdatingMemberUse
                  }
                  onClick={handleSubmit(onSave)}
                >
                  {'Save'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
      {showModalSelectMedia ? (
        <>
          <SelectMediaModal
            isOpen={showModalSelectMedia}
            onClose={onCloseModalSelectMedia}
            mediaType={EMediaType.IMAGE}
            category={EMediaCategory.HOME_PAGE}
            onSelectMedia={onSelectMedia}
          />
        </>
      ) : (
        <></>
      )}
    </AppModal>
  )
}

const InputWrapperStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 16px;

  & > div {
    min-width: 500px;
    grid-column: span 4 / span 4;
  }

  .textarea {
    margin-top: 10px;
  }

  .Input__field-container {
    width: 100%;
  }
`
